import type { ItemWarning, ItemWarningSeverity } from '@ixit/dof-core';

export type { ItemWarning, ItemWarningSeverity };

/**
 * Mirrors backend StepdrillItemDetail (server-eval'd payload on ItemState.Detail).
 * Numeric fields arrive as JSON numbers (double on the wire, even though backend
 * uses decimal for the math).
 */
export type StepdrillMeasurementMode = 'Relative' | 'AbsoluteFromTip' | 'AbsoluteFromStep1';

/** Who writes the segment list: the customer in the grid, or the block's formulas. */
export type StepdrillSegmentSource = 'customer' | 'formulas';

export interface StepdrillDetail {
    /** The source in effect: formulas while the block's Formlerna gäller när holds or waits, the customer when it is
     *  false. Absent on a payload from a server that predates it, which means the customer. */
    segmentSource?: StepdrillSegmentSource;
    /** What a waiting segment formula, Formlerna gäller när, or a waiting prefilled field waits on, by name; absent
     *  when none waits, and under production mode. The server builds no row while one waits. */
    waitingOn?: string[] | null;
    /** The prefilled fields the tool is drawn from no row for (empty, failing, waiting, an angle of 0 or less), by
     *  their labels; absent when none, and under production mode. */
    stoppedAt?: string[] | null;
    segmentCount: number;
    totalSetupTime: number;
    totalMachineTime: number;
    tipLength: number;       // drill tip cone axial length (mm)
    tipAngle: number;        // effective point angle (°)
    profileLength: number;   // GROUND profile: Σ transition + Σ cylinder (apex → end of last step,
                             // before taper/shank) — the tip IS row 0's transition, so it is not
                             // added again. NOT the finished tool's total length
                             // (that is the outer calculator's own totalLength = shank + flute + clearance).
    measurementMode: StepdrillMeasurementMode;  // current effective mode (echo of end-user or power-user choice)
    segments: StepdrillSegmentResult[];
    shankDiameterError?: string;  // non-fatal: ShankDiameterFormula threw (e.g. typo'd alias); segments still render

    // ---- Drill-depth crossing geometry (Stage 1.5; SVG consumes these later) ----
    approachAngle?: number;        // effective shank-approach angle (°)
    approachAngleMin?: number;     // admin lower bound; effective angle clamped here (Stage 2)
    approachAngleMax?: number;     // admin upper bound; effective angle clamped here (Stage 2)
    approachTaperLength?: number;  // axial length of the implicit final taper (0 = none / explicit)
    shankLength?: number;          // cylindrical shank-portion length (ShankLengthFormula); 0 = unknown → stub fallback
    totalLength?: number;          // finished tool overall length (TotalLengthFormula); 0 = unset → profile+shank fallback
    terminalDiameter?: number;     // shank Ø the final taper rises to
    drillDepth?: number;           // consumed total drill depth from tip (0 = not supplied)
    diameterAtDrillDepth?: number; // hole's Ø where the depth crosses the final chamfer (grey/reference)
    shankReachedAt?: number;       // axial pos where the tool reaches the shank Ø
    /** Cells that break a rule, addressed by row + column. The row keeps its VALUE and the item
     *  keeps its status: this MARKS, it does not gate. Absent under production mode. */
    segmentIssues?: StepdrillSegmentIssue[];
    /** What a declared rule allows in a cell, this evaluation — the guidance an EMPTY cell shows
     *  before anything is typed. Live: a bound that depends on another row moves when it does. */
    segmentBounds?: StepdrillSegmentBound[];
    incompleteSegments?: number[]; // R6: 0-based indices of segments collapsed to zero extent / no Ø —
                                   // BLOCKING (item is AwaitingInput); the customer must enter a real length.
                                   // A RESOLVED angle-only final row after ≥1 cut is exempt (drawn chamfer:
                                   // flat shoulder / zero Ø-delta is complete by design)

    // ---- p+{n} nomenclature part count (issue 06 stage B / 11) ----
    partCount?: number;            // n = parts in the tool's FORM (cylinders + collapsed cone runs),
                                   // NOT segmentCount (drawn rows). See NamePart below.
    nameParts?: NamePart[];        // the breakdown behind partCount, tip → shank; always partCount long
    maxSegments?: number | null;   // the segment CAP this configurator declares, null/absent = no
                                   // cap (stegborr-segmentmodellen/13). 1 = one-diameter tool, and
                                   // the surface strips itself: no add/remove, and nothing that
                                   // only means something BETWEEN steps.
    partsOutsideDrillDepth?: NamePart[];
                                   // the tail the DRILL DEPTH cut away (issue 23) — drawn parts that
                                   // begin outside the depth and so do not count. Empty when nothing
                                   // was cut. Rendered dimmed beside the counted ones, so a count
                                   // that dropped says what it dropped.
}

/** Mirrors backend `StepdrillSegmentBound`. */
export interface StepdrillSegmentBound {
    segmentIndex: number;
    column: string;
    min?: number | null;
    max?: number | null;
}

/** Mirrors backend `StepdrillSegmentIssue`. */
export interface StepdrillSegmentIssue {
    segmentIndex: number;
    /** The segment key whose cell is marked — `diameter`, `pos`, … */
    column: string;
    message: string;
    /** How hard it bites. Row-addressed messages are the ONLY ones that survive production mode,
     *  so without this a blocking break would render as an advisory in front of the customer. */
    severity: ItemWarningSeverity;
}

/**
 * One part of the p+{n} sequence, tip → shank. `diameter` = a drilling cylinder; `cone` = a RUN of
 * one or more consecutive rising transitions with no cylinder between them — the whole run is ONE
 * part (issue 21). The shank chamfer is part of the sequence when it begins inside the drill depth,
 * drawn or derived.
 */
export interface NamePart {
    kind: 'diameter' | 'cone';
    segmentIndexes: number[];  // rows the part was drawn on, tip → shank; -1 = the DERIVED shank chamfer
    value: number;             // Ø in mm for `diameter`; the run's FIRST angle in ° for `cone`
    cornerRadius: number;      // non-zero → a cone in the run carries a radius the count ignores (issue 11)
    angles?: number[];         // every angle in a cone run, tip → shank; absent on a diameter part
    isShankChamfer?: boolean;  // the run ends in the chamfer up to the shank Ø
}

/**
 * Mirrors backend `SegmentTolerance` — the row's tolerance level (stegborr-segmentmodellen 17 —
 * toleransnivåer), which is a ZONE: `lower` and `upper` are the two deviations from nominal in mm,
 * signed, and a fit class puts BOTH above nominal (k7 on Ø3,1 is +0,001 … +0,013). A ± level is the
 * symmetric case of the same thing.
 *
 * `value` is the ± HALF-width and `width` the zone width (twice `value`) — the pair an author
 * confuses, so both travel rather than being recomputed here. `standard` is the ISO 2768 figure (±)
 * for the row's Ø under the port's class whatever the cell says, i.e. the EMPTY cell's placeholder,
 * and `applies` whether any feature on the row drills — a row that never touches the hole wall has
 * its cell dimmed, never hidden. Every number is null (not 0) when nothing could be derived.
 *
 * The four zone members are optional so a payload from a server that predates them still types.
 */
export interface SegmentToleranceResult {
    value: number | null;
    lower?: number | null;
    upper?: number | null;
    width?: number | null;
    source: 'egen' | 'klass' | 'standard';
    class: string;
    standard: number | null;
    applies: boolean;
}

export interface StepdrillSegmentResult {
    index: number;
    input: Record<string, unknown>;
    /** The ROW's tolerance level — the single-value reading of the cell, which on a split cell is the
     *  CYLINDER's half; absent only on a payload that predates it.
     *
     *  `| null` for the same reason as `coneTolerance` below — see there. */
    tolerance?: SegmentToleranceResult | null;
    /** The CONE's tolerance, and ONLY when the cell splits (stegborr-segmentmodellen 22 — toleransen
     *  per feature): two values, cone first — `k7 / ±0,05` — when the cone that gives the hole its
     *  bottom is ground harder than the cylinder above it. ABSENT means "not split": the cone is held
     *  to `tolerance` too, which is every cell in the corpus.
     *
     *  **`| null` as well as `?`, and that is not belt-and-braces.** "Absent" is what the SERVER
     *  means; what arrives is whatever its serializer wrote, and a C# null only disappears from the
     *  JSON if the member is told to (`JsonIgnore(WhenWritingNull)` on `SegmentResult`). It was not,
     *  and `"coneTolerance": null` reached a reader that tested `!== undefined` — true for null —
     *  and then read a property off it, which threw and took the whole segment table's render down
     *  on every row in the corpus. Typing this as optional-only is what let that compile.
     *
     *  So: a nullable wire field is `?: T | null` here, and the reader normalises the two to one at
     *  the boundary (`?? undefined`) rather than every use site guessing which arrived. */
    coneTolerance?: SegmentToleranceResult | null;
    /** Operation name → measure key → minutes. An operation carries as many measures as its
     *  author gave it; `setupTime` and `machineTime` are RESERVED and the server writes both into
     *  every result whatever was authored, so a reader can rely on them and must not treat a
     *  further key as an error. See `operation-measures.ts` in dof-core. */
    operationResults: Record<string, Record<string, number>>;
    transitionLength: number;  // axial length of the cone INTO this segment; on i=0 that cone is the
                               // tip, so this is tipLength (stegborr-segmentmodellen 19 — spetsen är
                               // segment 1:s kon)
    cylinderLength: number;    // canonical cylinder length = the Relative-mode expression
    posFromTip: number;        // segment-end position from tip = the AbsoluteFromTip expression
    posFromStep1: number;      // = posFromTip − tipLength = the AbsoluteFromStep1 expression
}

/**
 * Shape of a Stepdrill ItemState as received by the preview renderer. Mirrors
 * the generic Abstractions.ItemState from Ixit.Dof.Core with the Stepdrill-
 * specific Detail.
 */
export interface StepdrillItemState {
    itemType: 'stepdrill';
    alias: string;
    visible: boolean;
    status: string;
    errorMessage?: string;
    detail?: StepdrillDetail;
    /** Non-fatal verdicts with their severity (Core ItemState.Warnings). Lives on the state rather
     *  than in `detail` because the commercial gate has to read it and `detail` is plugin-shaped. */
    warnings?: ItemWarning[];
    /** Server-rendered, self-contained silhouette SVG (Core ItemState.SilhouetteSvg).
     *  Present when a visualiser is registered server-side. The client renders it
     *  inline; no geometry on the client. */
    silhouetteSvg?: string;
    /** Named view projections beyond the default silhouette (Core ItemState.ProjectionSvgs).
     *  The rotary family supplies "hole" = the bore the tool makes (the hole|tool toggle). */
    projectionSvgs?: Record<string, string>;
}

/** Default per-segment shape end-users edit. */
export interface SegmentInput {
    diameter: number;
    length: number;
    incomingAngle: number;
    incomingCornerRadius: number;
    tool: string;
    /** The Tol. cell, FREE TEXT as typed: '' = the standard applies, '±0,05' / '0,05' = an own
     *  value, 'k7' / 'h6' = a fit class. The server parses it; the client carries it verbatim. */
    tolerance: string;
}

export function blankSegment(): SegmentInput {
    return {
        diameter: 0,
        length: 0,
        incomingAngle: 0,
        incomingCornerRadius: 0,
        tool: '',
        tolerance: '',
    };
}
